import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Card,
  CardHeader,
  Chip,
  Container,
  List,
  Stack
} from "@mui/material";

import Box from "@mui/material/Box";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import { ProgressButton } from "@qubit/autoparts";
import JsonView from "@uiw/react-json-view";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppSelector, useAppDispatch } from "~/app/store";
import { useGetWorkstationsQuery } from "~/redux/warehouse/workstation.hooks";

import { ActivityIndicator } from "./ActivityIndicator";
import { EventChip } from "./EventChip";
import EventInfoBox from "./EventInfoBox";
import WorkstationSelectorModal from "./WorkstationSelectorModal";
import {
  selectLooseThreadEvents,
  selectEventHistoryByWorkstationIdMap
} from "./andon.selectors";
import { setEventExplorerIds, selectEventExplorerIds } from "./andon.slice";

import { AndonEventWithMeta } from "./andon.types";

export function EventExplorer() {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const [focusedEvent, setFocusedEvent] = useState<AndonEventWithMeta | null>(
    null
  );
  const eventExplorerIds = useAppSelector(selectEventExplorerIds);
  const eventHistoryByWorkstationId = useAppSelector(
    selectEventHistoryByWorkstationIdMap(eventExplorerIds)
  );
  const looseThreadEvents = useAppSelector(selectLooseThreadEvents);

  const { data: workstations } = useGetWorkstationsQuery();

  const [workstationSelectorOpen, setWorkstationSelectorOpen] = useState(false);

  return (
    <Container>
      <Stack gap={4}>
        {/* buttons container */}
        <Stack direction="row" gap={4}>
          <ProgressButton
            emphasis="high"
            responsive
            variant="contained"
            color="secondary"
            onClick={() => {
              setWorkstationSelectorOpen(true);
            }}
          >
            {t("select workstations")}
          </ProgressButton>
          <Box height={25} width={100}>
            <ActivityIndicator />
          </Box>
        </Stack>
        <Stack direction="row" gap={2}>
          {/* first column */}
          <List sx={{ width: "32rem" }}>
            {(workstations ?? [])
              .filter((ws) => eventExplorerIds.includes(ws.id))
              .slice()
              .sort(
                (a, b) =>
                  a.autostoreGridName.localeCompare(b.autostoreGridName) ||
                  a.deviceId.localeCompare(b.deviceId)
              )
              .map((ws) => {
                const workstation = ws;
                const id = ws.id;
                const ports = ws.ports;
                const parentPortId = ports[0]?.parentPortId ?? null;
                const events = eventHistoryByWorkstationId[id] ?? [];

                return (
                  <Accordion component="li" key={workstation.id}>
                    <AccordionSummary
                      expandIcon={<ArrowDropDownIcon />}
                      aria-labelledby={`workstation-${workstation.deviceId}`}
                    >
                      <Stack flexGrow={1} gap={1} paddingRight={1}>
                        <Box display="flex" flexGrow={1}>
                          <Typography
                            id={`workstation-${workstation.deviceId}`}
                          >
                            {workstation.deviceId}
                          </Typography>
                          <Stack
                            direction="row"
                            justifySelf="flex-end"
                            marginLeft="auto"
                            gap={1}
                          >
                            {ports.map((port) => (
                              <Chip
                                key={`port-${port.portId}`}
                                color="1"
                                label={port.portId}
                                size="small"
                              />
                            ))}
                            {parentPortId !== null && (
                              <Chip
                                color="2"
                                label={parentPortId}
                                size="small"
                              />
                            )}
                          </Stack>
                        </Box>
                        <Stack direction="row">
                          {events.length === 0 && (
                            <Typography color="textSecondary">
                              {t("no events")}
                            </Typography>
                          )}
                          {events.slice(0, 10).map((andonEvent, i) => (
                            <EventChip
                              key={`${
                                typeof andonEvent.timestamp === "string"
                                  ? andonEvent.timestamp
                                  : andonEvent.timestamp.toISOString()
                              }x${i}`}
                              event={andonEvent}
                            />
                          ))}
                        </Stack>
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box
                        sx={{
                          display: "flex",
                          padding: "5px",
                          flexWrap: "wrap",
                          justifyContent: "space-between",
                          height: "300px",
                          overflowY: "scroll",
                          marginTop: "10px"
                        }}
                      >
                        {events.map((ae, index) => (
                          <EventInfoBox
                            key={`andonEvent-${index}`}
                            andonEvent={ae}
                            onClickCb={() => setFocusedEvent(ae)}
                          />
                        ))}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                );
              })}

            {/* Loose Threads */}
            <Accordion component="li">
              <AccordionSummary
                expandIcon={<ArrowDropDownIcon />}
                aria-label="Loose Threads"
              >
                <Stack gap={1}>
                  <Typography>Loose Threads</Typography>
                  <Stack direction="row">
                    {looseThreadEvents.length === 0 && (
                      <Typography color="textSecondary">No Events</Typography>
                    )}
                    {looseThreadEvents.slice(0, 10).map((andonEvent, i) => (
                      <EventChip
                        key={`${i}a${
                          typeof andonEvent.timestamp === "string"
                            ? andonEvent.timestamp
                            : andonEvent.timestamp.toISOString()
                        }`}
                        event={andonEvent}
                      />
                    ))}
                  </Stack>
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                <Box
                  sx={{
                    display: "flex",
                    padding: "5px",
                    flexWrap: "wrap",
                    justifyContent: "space-between",
                    height: "300px",
                    overflowY: "scroll",
                    marginTop: "10px"
                  }}
                >
                  {looseThreadEvents.map((ae, index) => (
                    <EventInfoBox
                      key={`looseThread-${index}`}
                      andonEvent={ae}
                      onClickCb={() => setFocusedEvent(ae)}
                    />
                  ))}
                </Box>
              </AccordionDetails>
            </Accordion>
          </List>

          {/* second column */}
          <Card sx={{ my: 1, flexGrow: 1 }}>
            <CardHeader title={t("focused event")} />
            <CardContent>
              {focusedEvent ? (
                <JsonView
                  value={focusedEvent || {}}
                  collapsed={3}
                  indentWidth={3}
                  displayDataTypes={false}
                />
              ) : (
                <Typography color="textSecondary">
                  {t("select an event to view details")}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Stack>
      </Stack>
      {/* modals */}
      <WorkstationSelectorModal
        isOpen={workstationSelectorOpen}
        selectedWorkstationIds={eventExplorerIds}
        workstationSelectorCb={(newIds) => {
          dispatch(setEventExplorerIds(newIds));
        }}
        setModalOpen={(isOpen) => setWorkstationSelectorOpen(isOpen)}
      />
    </Container>
  );
}

export default EventExplorer;
